@use '../../style/mixins';

/**
* @prop --chip-max-width: Maximum width of the chip. Defaults to `10rem`. Keep in mind that the chips should not appear too big.
* @prop --chip-progress-color: Color of the progress bar. Defaults to `rgb(var(--contrast-700))`.
* @prop --chip-readonly-border-color: Color of the border in readonly state. Defaults to `rgb(var(--contrast-800), 0.5)`.
*/

:host(limel-chip) {
    --limel-chip-height: var(--limel-chip-size, 1.75rem);
    --limel-chip-gap: 0.5rem;
    isolation: isolate;
    display: inline-flex;
    align-items: center;
    min-width: 0;
}

:host(limel-chip[size='small']) {
    --limel-chip-height: 1.5rem;
    --limel-chip-gap: 0.25rem;
}

* {
    box-sizing: border-box;
}

.chip {
    all: unset;
    position: relative;

    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;

    max-width: min(var(--chip-max-width, 100%), 25rem);
    height: var(--limel-chip-height);

    border-radius: var(--limel-chip-height);
    font-size: clamp(
        0.8125rem /* 13px */,
        calc(var(--limel-chip-height) - 0.875rem /* 14px */),
        1rem
    );
    padding: 0 0.125rem;

    &:has(limel-icon),
    &:has(img) {
        .text {
            padding-left: 0;
        }
    }

    &:not([disabled]) {
        @include mixins.visualize-keyboard-focus;
        @include mixins.is-elevated-inset-clickable;
    }

    &:has(limel-badge) {
        padding-right: 0.375rem;

        .text {
            padding-right: 0;
        }
    }

    &:has(+ .remove-button:hover) {
        box-shadow: var(--shadow-depth-8-error);
    }

    &:has(+ limel-menu),
    &:has(+ .trailing-button) {
        padding-right: calc(var(--limel-chip-height) + 0.125rem);

        .text {
            padding-right: 0;
        }
    }
}

:host(limel-chip[disabled]:not([disabled='false'])) {
    .chip {
        // Similar to `limel-button[disabled]`
        color: rgba(var(--contrast-1600), 0.37);
        background-color: rgba(var(--contrast-1600), 0.1);
        box-shadow: none;
    }
}

:host(limel-chip[readonly]:not([readonly='false'])) {
    .chip {
        box-shadow: 0 0 0 1px
            var(--chip-readonly-border-color, rgb(var(--contrast-800), 0.5));
    }
}

:host(limel-chip[selected]:not([selected='false'])) {
    .chip {
        box-shadow: var(--button-shadow-inset);

        &:focus-visible {
            box-shadow:
                var(--button-shadow-inset), var(--shadow-depth-8-focused);
        }

        &:active {
            box-shadow: var(--button-shadow-inset-pressed);
        }
    }
    .text {
        color: var(--lime-primary-color, var(--limel-theme-primary-color));
    }
}

:host(limel-chip[type='filter']) {
    .chip {
        border-top-left-radius: 0.5rem;
        border-bottom-left-radius: 0.5rem;

        &:after {
            content: '';
            transition:
                background-color 0.4s ease 0.2s,
                box-shadow 0.6s ease 0.2s;
            box-sizing: border-box;
            position: absolute;
            bottom: 0.125rem;
            left: 0.125rem;
            width: 0.5rem;
            height: 0.5rem;
            border-radius: 50%;
            background-color: rgb(var(--contrast-800), 0.8);
            box-shadow: 0 0 0 1px rgb(var(--color-white)) inset;
        }
    }
}

:host(limel-chip[type='filter'][selected]) {
    .chip {
        &:after {
            background-color: rgb(var(--color-green-default));
            box-shadow:
                0 0 0.375rem 0 rgb(var(--color-green-light)),
                0 0 0 1px rgb(var(--color-white)) inset;
        }
    }
}

:host(limel-chip[invalid]) {
    .chip {
        @include mixins.add-chessboard-background();
        background-color: rgb(var(--color-red-default));
    }

    .text {
        color: rgb(var(--color-white));
    }
}

img,
limel-icon {
    flex-shrink: 0;
    width: calc(var(--limel-chip-height) - 0.25rem);
    height: calc(var(--limel-chip-height) - 0.25rem);
}

img {
    object-fit: cover;
    border-radius: 50%;
}

limel-icon {
    padding: 0.0625rem;
}

limel-badge {
    pointer-events: none;
}

.text {
    @include mixins.truncate-text;
    line-height: 1.2;
    padding: 0 0.5rem;
    font-size: var(--limel-theme-default-font-size);
}

.trailing-button {
    all: unset;
    @include mixins.is-flat-clickable();

    z-index: 1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    margin-left: calc(var(--limel-chip-height) * -1 + 0.125rem);
    margin-right: 0.125rem;
    width: calc(var(--limel-chip-height) - 0.25rem);
    height: calc(var(--limel-chip-height) - 0.25rem);

    border-radius: 50%;

    svg {
        transition:
            color 0.2s ease,
            transform 0.2s ease;
        width: 1.25rem;
    }

    &.remove-button:hover {
        color: rgb(var(--color-red-dark));
        svg {
            transform: scale(0.8);
        }
    }
}

limel-menu {
    button[slot='trigger'] {
        @include mixins.visualize-keyboard-focus;
    }

    &[open] {
        button[slot='trigger'] {
            box-shadow: var(--button-shadow-inset);
        }
    }
}

@import './partial-styles/_loading.scss';
@import './partial-styles/_progress.scss';
